import {
    ICity,
    ICorporation,
    ICountry,
    IMill,
    IState,
} from "../../pages/Osorno/OsornoSetupDashboard/Corporations/CorporationsAPIInterfaces";
import {
    ApolloError,
    ApolloQueryResult,
    OperationVariables,
    MutationFunctionOptions,
    DefaultContext,
    ApolloCache,
    FetchResult,
} from "@apollo/client";
import { IRegion } from "../../pages/Osorno/OsornoSetupDashboard/Corporations/CorporationForm/CorporationFormInterfaces";
import { ICorporateDashboardFilters } from "pages/Corporate/CorporateDashboard/useCorporateDashboardFilters";
import { SummaryOption } from "constants/constants";
import { IShiftHistoric } from "pages/Corporate/CorporateSetupDashboard/Corporations/MillSettingsStep/MillSettingsStepInterfaces";
import { IKeySuccessFactor } from "entities/RCA/RCALibraryEntity/RCALibraryEntityInterfaces";
import {
    IDefaultFailureTypeTranslations,
    IFailureType,
} from "contexts/HMICachedDataContext/HMICachedDataContextInterfaces";

export interface ICorporationsEntity {
    GetCorporations: (user_type_id: number) => IGetCorporationsReturn;
    GetCorporationCountryCode: (corporate_id: number) => ICorporationCountryCodeReturn;
    GetCountries: () => IGetCountriesReturn;
    GetStates: (country_id: number) => IGetStatesReturn;
    GetCities: (state_id: number) => IGetCitiesReturn;
    InsertCorporationWithAdmin: () => IInsertCorporationWithAdminReturn;
    GetCorporationAndMillBackOffice: (millId?: number) => IGetCorporationAndMillBackOfficeReturn;
    GetMenuCorporateDivisionsRegionsMills: (skip?: boolean) => IGetMenuCorporateDivisionsRegionsMillsReturn;
    UpsertCorporateDataOnboardingBO: () => IUpsertCorporateDataOnboardingBOReturn;
    ActivateCorporateModuleLicenseOsornoBO: () => IActivateCorporateModuleLicenseOsornoBOReturn;
    DeactivateOrReactivateCorporateModuleLicenseOsornoBO: () => IDeactivateOrReactivateCorporateModuleLicenseOsornoBOReturn;
    UpdateCorporateRcaEnabledOsornoBO: () => IUpdateCorporateRcaEnabledOsornoBOReturn;
    GetCorporationDetail: (corporate_id: number) => IGetCorporationDetailReturn;
    GetCorporationAdmins: (corporate_id: number, user_type_id: number) => IGetCorporationAdminsReturn;
    GetDowntimeSummaryByFailureTypeCorporateDashboard: (
        filters: ICorporateDashboardFilters,
        corporateId: number,
    ) => IGetDowntimeSummaryByFailureTypeCorporateDashboardReturn;
    GetDowntimeSummaryByFailureCodeCorporateDashboard: (
        filters: ICorporateDashboardFilters,
        corporateId: number,
        failureTypeId?: number,
    ) => IGetDowntimeSummaryByFailureCodeCorporateDashboardReturn;
    GetDowntimeSummaryBySecondaryFailureCodeTotalsCorporateDashboard: (
        filters: ICorporateDashboardFilters,
        corporateId: number,
        failureCodeId?: number,
    ) => IGetDowntimeSummaryBySecondaryFailureCodeTotalsCorporateDashboardReturn;
    GetTotalDowntimeSummaryByTypeAllMillsCorporateDashboard: (
        filters: ICorporateDashboardFilters,
        summaryOption: SummaryOption,
        corporateId: number,
        id?: number,
    ) => IGetTotalDowntimeSummaryByTypeAllMillsCorporateDashboardReturn;
    GetTotalDowntimeSummaryByCodeOrSecondaryCodeAllMillsCorporateDashboard: (
        filters: ICorporateDashboardFilters,
        summaryOption: SummaryOption,
        corporateId: number,
        codeId?: number,
        secondaryCodeId?: number,
    ) => IGetTotalDowntimeSummaryByCodeOrSecondaryCodeAllMillsCorporateDashboardReturn;
    UpsertCorporationWithAdminMaderaBackoffice: () => IUpsertCorporationWithAdminMaderaBackofficeReturn;
    GetCorporateAllDivisionAllRegionsAllMills: () => IGetCorporateAllDivisionAllRegionsAllMillsReturn;
    GetAllKeySuccessFactors: () => IGetAllKeySuccessFactorsReturn;
    GetAllFailureTypes: () => IGetAllFailureTypesReturn;
    UpdateCorporateDashboardFeature: () => {
        error: ApolloError | undefined;
        activateCorporateDashboard: (corporateId: number) => Promise<FetchResult<any>>;
        deactivateCorporateDashboard: (corporateId: number) => Promise<FetchResult<any>>;
        loading: boolean;
    };
}

export interface ICorporationCorporateBO {
    corporate_id: number;
    name: string;
    address_1: string;
    phone_number: string;
    users: {
        email: string;
        first_name: string;
        last_name: string;
        user_type: {
            user_type_id: number;
            name: string;
            default_user_type_translations: {
                user_type_id: number;
                language: {
                    language_id: number;
                    name: string;
                    code: string;
                };
                translated_name: string;
            }[];
        };
    }[];
    corporate_data: {
        corporate_data_id?: number;
        url: string;
        holding_company: {
            holding_company_id?: number;
            name: string;
            url: string;
            address: string;
        };
    };
    divisions: {
        __typename?: string;
        division_id: number;
        name: string;
        deleted_at?: Date;
        regions: {
            __typename?: string;
            region_id?: number;
            name: string;
            deleted_at?: Date;
            mills?: { mill_id: number }[];
        }[];
    }[];
}

export interface ICorporationCountryCodeReturn {
    loading: boolean;
    corporationCountryCode: string;
    apolloQueryError: ApolloError | undefined;
    refetch: (variables?: Partial<OperationVariables> | undefined) => Promise<ApolloQueryResult<any>>;
}

export interface IMillCorporateBOSetup {
    mill_id: number;
    corporate_id: number;
    name?: string;
    mill_data: {
        mill_data_id?: number;
        language_id: number;
        first_day_reporting_week: number;
        day_reporting_week?: {
            day_id: number;
            name: string;
            default_day_translations: {
                default_day_translation_id: number;
                language: {
                    language_id: number;
                    name: string;
                };
                translated_name: string;
            }[];
        };
        measure_metric_id: number;
        timezone: string;
        first_day_fiscal_year?: string;
    };
    crews: { crew_id: number; name: string }[];
    mill_key_success_factors: {
        mill_key_success_factors_id?: number;
        is_enabled?: boolean;
        key_success_factor: {
            corporate_id?: number;
            key_success_factors_id: number;
            name: string;
            default_key_success_factor_translations: {
                key_success_factor_id: number;
                language: {
                    language_id: number;
                    name: string;
                    code: string;
                };
                translated_name: string;
            }[];
        };
    }[];
    production_lines: {
        production_line_id: number;
        production_line_shifts: {
            production_line: {
                production_line_id: number;
                name: string;
            };
            shift: {
                shift_id: number;
                name: string;
                shift_historic: {
                    shift_historic_id: number;
                    end_time: string;
                    start_time: string;
                    effective_at: Date;
                    shift_days: {
                        shift_day_id?: number;
                        day_id: number;
                        is_enabled: boolean;
                        day: {
                            name: string;
                            default_day_translations?: {
                                default_day_translation_id: number;
                                language: {
                                    language_id: number;
                                    name: string;
                                };
                                translated_name: string;
                            }[];
                        };
                    }[];
                }[];
            };
        }[];
    }[];
    shifts: {
        shift_id: number;
        name: string;
        shift_historic: {
            shift_historic_id: number;
            end_time: string;
            start_time: string;
            effective_at: Date;
            shift_days: {
                shift_day_id?: number;
                day_id: number;
                is_enabled: boolean;
            }[];
        }[];
        production_line_shifts: {
            production_line_shift_id: number;
            production_line: {
                production_line_id: number;
                name: string;
            };
        }[];
    }[];
}

export interface IShift {
    shift_id?: number;
    name: string;
    deleted_at?: Date;
    created_at?: Date;
    shift_historic: IShiftHistoric;
}

export interface IGetCorporationAndMillBackOfficeReturn {
    loading: boolean;
    corporation: ICorporationCorporateBO;
    mill: IMillCorporateBOSetup;
    error: ApolloError | undefined;
    refetch: (variables?: Partial<OperationVariables> | undefined) => Promise<ApolloQueryResult<any>>;
}

export interface IInsertCorporationWithAdmin {
    address_1: string;
    address_2?: string;
    state_name: string | undefined | null;
    city_name: string | undefined | null;
    country_id: number | undefined;
    name: string;
    phone_number: string;
    zip_code: string;
    divisions?: { data: { name: string; regions: { data: IRegion[] } }[] };
    users?: { data: { first_name: string; last_name: string; email: string; user_type_id?: number }[] };
}

export interface IUser {
    first_name: string;
    last_name: string;
    email: string;
}

export interface IGetCorporationsReturn {
    loading: boolean;
    corporations: ICorporation[];
    apolloQueryError: ApolloError | undefined;
    refetch: (variables?: Partial<OperationVariables> | undefined) => Promise<ApolloQueryResult<any>>;
}

export interface IGetCountriesReturn {
    loading: boolean;
    countries: ICountry[];
    apolloQueryError: ApolloError | undefined;
    refetch: (variables?: Partial<OperationVariables> | undefined) => Promise<ApolloQueryResult<any>>;
}

export interface IGetStatesReturn {
    loading: boolean;
    states: IState[];
    apolloQueryError: ApolloError | undefined;
    refetch: (variables?: Partial<OperationVariables> | undefined) => Promise<ApolloQueryResult<any>>;
}

export interface IGetCitiesReturn {
    loading: boolean;
    cities: ICity[];
    apolloQueryError: ApolloError | undefined;
    refetch: (variables?: Partial<OperationVariables> | undefined) => Promise<ApolloQueryResult<any>>;
}

export interface IInsertCorporationWithAdminReturn {
    apolloMutationError: ApolloError | undefined;
    addCorporationWithAdmin: (
        options?: MutationFunctionOptions<any, OperationVariables, DefaultContext, ApolloCache<any>> | undefined,
    ) => Promise<FetchResult<any, Record<string, any>, Record<string, any>>>;
}

export interface IUpsertCorporateDataOnboardingBOReturn {
    apolloMutationError: ApolloError | undefined;
    upsertCorporateData: (
        options?: MutationFunctionOptions<any, OperationVariables, DefaultContext, ApolloCache<any>> | undefined,
    ) => Promise<FetchResult<any, Record<string, any>, Record<string, any>>>;
}

export interface IGetMenuCorporateDivisionsRegionsMillsReturn {
    loading: boolean;
    corporations: ICorporation[];
    millsWithNoDivision: { name: string; mill_id: number }[];
    error: ApolloError | undefined;
    refetch: (variables?: Partial<OperationVariables> | undefined) => Promise<ApolloQueryResult<any>>;
}

export interface IActivateCorporateModuleLicenseOsornoBOReturn {
    apolloMutationError: ApolloError | undefined;
    activateCorporateModule: (
        options?: MutationFunctionOptions<any, OperationVariables, DefaultContext, ApolloCache<any>> | undefined,
    ) => Promise<FetchResult<any, Record<string, any>, Record<string, any>>>;
}

export interface IDeactivateOrReactivateCorporateModuleLicenseOsornoBOReturn {
    apolloMutationError: ApolloError | undefined;
    deactivateOrReactivateCorporateModule: (
        options?: MutationFunctionOptions<any, OperationVariables, DefaultContext, ApolloCache<any>> | undefined,
    ) => Promise<FetchResult<any, Record<string, any>, Record<string, any>>>;
}

export interface IUpdateCorporateRcaEnabledOsornoBOReturn {
    apolloMutationError: ApolloError | undefined;
    updateCorporateRcaEnabled: (
        options?: MutationFunctionOptions<any, OperationVariables, DefaultContext, ApolloCache<any>> | undefined,
    ) => Promise<FetchResult<any, Record<string, any>, Record<string, any>>>;
}

export interface IGetCorporationDetailReturn {
    loading: boolean;
    corporate?: ICorporation;
    millsWithNoDivision: IMill[];
    error: ApolloError | undefined;
    refetch: (variables?: Partial<OperationVariables> | undefined) => Promise<ApolloQueryResult<any>>;
}

export interface IGetCorporationAdminsReturn {
    loading: boolean;
    corporationAdmins: IUser[];
    error: ApolloError | undefined;
    refetch: (variables?: Partial<OperationVariables> | undefined) => Promise<ApolloQueryResult<any>>;
}

export interface IUpsertCorporationWithAdminMaderaBackofficeReturn {
    apolloMutationError: ApolloError | undefined;
    upsertCorporationWithAdmin: (
        options?: MutationFunctionOptions<any, OperationVariables, DefaultContext, ApolloCache<any>> | undefined,
    ) => Promise<FetchResult<any, Record<string, any>, Record<string, any>>>;
}

export interface IDowntimeSummaryByFailureType {
    failure_type_id: number;
    name: string;
    color_code: string;
    default_failure_type_translations: {
        failure_type_id: number;
        language: {
            language_id: number;
            name: string;
            code: string;
        };
        translated_name: string;
    }[];
    is_location_specific: boolean;
    failure_codes: {
        failure_code_id: number;
        name: string;
    }[];
    event_failure_code_summary_per_mcs_aggregate: {
        aggregate: {
            sum: {
                occurrences: number;
                seconds: number;
            };
        };
    };
}

export interface IDowntimeSummaryByFailureCode {
    failure_code_id: number;
    name: string;
    default_failure_code_translations: {
        failure_code_id: number;
        language: {
            language_id: number;
            name: string;
            code: string;
        };
        translated_name: string;
    }[];
    failure_type: {
        color_code: string;
    };
    event_failure_code_summary_per_mcs_aggregate: {
        aggregate: {
            sum: {
                seconds: number;
                occurrences: number;
            };
        };
    };
}

export interface IDowntimeSummaryBySecondaryFailureCodeTotals {
    total_seconds: number;
    total_occurrences: number;
    failure_data: {
        failure_code_id: number;
        name: string;
        default_failure_code_translations: {
            failure_code_id: number;
            language: {
                language_id: number;
                name: string;
                code: string;
            };
            translated_name: string;
        }[];
    };
}

export interface IDowntimeSummaryTotalsByTypeAllMills {
    total_seconds: number;
    total_occurrences: number;
    mill: {
        mill_id: number;
        name: string;
    };
    failure_type: {
        failure_type_id: number;
        color_code: string;
        name: string;
        default_failure_type_translations: IDefaultFailureTypeTranslations[];
    };
}

export interface IDowntimeSummaryTotalsByCodeAllMills {
    total_seconds: number;
    total_occurrences: number;
    mill: {
        mill_id: number;
        name: string;
    };
    failure_data: {
        failure_code_id: number;
        name: string;
        default_failure_code_translations: {
            failure_code_id: number;
            language: {
                language_id: number;
                name: string;
                code: string;
            };
            translated_name: string;
        }[];
    };
}

export interface IGetDowntimeSummaryByFailureTypeCorporateDashboardReturn {
    loading: boolean;
    downtimeSummaryByFailureType: IDowntimeSummaryByFailureType[];
    apolloQueryError: ApolloError | undefined;
    refetch: (variables?: Partial<OperationVariables> | undefined) => Promise<ApolloQueryResult<any>>;
}

export interface IGetDowntimeSummaryByFailureCodeCorporateDashboardReturn {
    loading: boolean;
    downtimeSummaryByFailureCode: IDowntimeSummaryByFailureCode[];
    apolloQueryError: ApolloError | undefined;
    refetch: (variables?: Partial<OperationVariables> | undefined) => Promise<ApolloQueryResult<any>>;
}

export interface IGetDowntimeSummaryBySecondaryFailureCodeTotalsCorporateDashboardReturn {
    loading: boolean;
    downtimeSummaryBySecondaryFailureCodeTotals: IDowntimeSummaryBySecondaryFailureCodeTotals[];
    apolloQueryError: ApolloError | undefined;
    refetch: (variables?: Partial<OperationVariables> | undefined) => Promise<ApolloQueryResult<any>>;
}

export interface IGetTotalDowntimeSummaryByTypeAllMillsCorporateDashboardReturn {
    loading: boolean;
    downtimeSummaryAllMills: IDowntimeSummaryTotalsByTypeAllMills[];
    downtimeSummaryTrailingPeriod: IDowntimeSummaryTotalsByTypeAllMills[];
    apolloQueryError: ApolloError | undefined;
    refetch: (variables?: Partial<OperationVariables> | undefined) => Promise<ApolloQueryResult<any>>;
}

export interface IGetTotalDowntimeSummaryByCodeOrSecondaryCodeAllMillsCorporateDashboardReturn {
    loading: boolean;
    downtimeSummaryAllMills: IDowntimeSummaryTotalsByCodeAllMills[];
    downtimeSummaryTrailingPeriod: IDowntimeSummaryTotalsByCodeAllMills[];
    apolloQueryError: ApolloError | undefined;
    refetch: (variables?: Partial<OperationVariables> | undefined) => Promise<ApolloQueryResult<any>>;
}

export enum TrailingTrendPeriodOptionsEnum {
    ONE_MONTH = "1_month",
    THIRTEEN_WEEK = "13_week",
    SIX_MONTH = "6_month",
    ONE_YEAR = "1_year",
}

export interface IGetCorporateAllDivisionAllRegionsAllMillsReturn {
    loading: boolean;
    corporations: ICorporation[];
    error: ApolloError | undefined;
    refetch: (variables?: Partial<OperationVariables> | undefined) => Promise<ApolloQueryResult<any>>;
}

export interface IGetAllKeySuccessFactorsReturn {
    loading: boolean;
    keySuccessFactors: IKeySuccessFactor[];
    error: ApolloError | undefined;
}

export interface IGetAllFailureTypesReturn {
    loading: boolean;
    failureTypes: IFailureType[];
    error: ApolloError | undefined;
}
